/**
 * HeaderMenu - a word in the header that opens a panel under it (v9.6.0).
 *
 * The header is four groups since v9.6.0 (Mode, Table, Play, Session), and
 * what does not need to be one press away hangs from a word: the Table's
 * sides and positions, the danger's pin, and the session's "more" (files,
 * settings, locks, hotkeys, the bug report). One pattern for all three:
 * the panel closes on Escape and on a press outside it, and its toggle
 * says whether it is open.
 *
 * @param {object} props
 * @param {React.ReactNode} props.label - the toggle's words
 * @param {string} props.testId - the toggle's
 * @param {string} props.panelTestId
 * @param {string} props.ariaLabel - the panel's name
 * @param {'dialog'|'menu'} [props.role] - a panel of controls, or a list of commands
 * @param {'left'|'right'} [props.align] - which edge of the toggle the panel hangs from
 * @param {string} [props.title], [props.className] - the toggle's tooltip and class
 * @param {React.ReactNode | ((close: () => void) => React.ReactNode)} props.children
 */
import { useCallback, useRef, useState } from 'react';
import styles from '../../styles/HeaderMenu.module.css';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useCloseOnOutsidePointer } from '../characterLibrary/useLibraryPanel';

export default function HeaderMenu(props) {
  const { label, testId, panelTestId, ariaLabel, role = 'dialog', align = 'left', title, className, children } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useCloseOnOutsidePointer(open, ref, close);
  useEscapeKey(open, close);
  return (
    <div className={styles.wrap} ref={ref}>
      <button
        type="button"
        className={className ?? styles.toggle}
        aria-expanded={open}
        aria-haspopup={role}
        data-testid={testId}
        title={title}
        onClick={() => setOpen((was) => !was)}
      >
        {label}
      </button>
      {open && (
        <div className={styles.panel} data-align={align} role={role} aria-label={ariaLabel} data-testid={panelTestId}>
          {typeof children === 'function' ? children(close) : children}
        </div>
      )}
    </div>
  );
}
